// Shared EPM workflow and display helpers.
import dayjs from 'dayjs';

export const STATUS_ORDER = ['IDEAS', 'PRIORITISED', 'PLANNING', 'BUILD_QA', 'SCHEDULED', 'LIVE', 'ANALYSING', 'CONCLUDED'];

export const STEP_LABELS = ['Idea', 'Priority', 'Plan', 'Build & QA', 'Schedule', 'Result'];

const STEP_FOR_STATUS = {
    IDEAS: 0, PRIORITISED: 1, PLANNING: 2, BUILD_QA: 3, SCHEDULED: 4, LIVE: 4, ANALYSING: 5, CONCLUDED: 5,
};

/** The workflow step a test is currently at. */
export const stepForStatus = (status) => STEP_FOR_STATUS[status] ?? 0;

/** Status a test moves to when the footer action of a step is taken. */
export const STATUS_AFTER_STEP = {
    0: 'PRIORITISED',
    1: 'PLANNING',
    2: 'BUILD_QA',
    3: 'SCHEDULED',
    4: 'LIVE',
    5: 'CONCLUDED',
};

/** The later of two statuses — the workflow only ever moves a test forward. */
export const laterStatus = (current, target) =>
    STATUS_ORDER.indexOf(target) > STATUS_ORDER.indexOf(current) ? target : current;

/** Whether a test has reached a workflow step. */
export const hasReachedStep = (test, stepIndex) => stepForStatus(test?.status) >= stepIndex;

export const structureOf = (test) => test?.ideation?.structure || {};

/** Tag ids on a test as strings, whether tag_ids holds ids or populated tags. */
export const tagIdsOf = (test) => (test?.tag_ids || []).map((tag) => String(tag?._id || tag));

export const ownerName = (test) => {
    return typeof test?.created_by === 'object' ? test?.created_by?.name : test?.created_by;
};

/** "Joana Mendes" → "JM"; single words and ids fall back to their first two letters. */
export const ownerInitials = (test) => {
    const name = ownerName(test) || '';
    const words = name.includes('|') ? [] : name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    const clean = name.includes('|') ? name.split('|')[1] : name;
    return (clean || '??').slice(0, 2).toUpperCase();
};

/** ICE as a number with one decimal, or null when the test hasn't been scored. */
export const iceScore = (test) => {
    const { impact = 0, confidence = 0, ease = 0 } = test?.priority || {};
    if (!impact && !confidence && !ease) return null;
    return Number(((impact + confidence + ease) / 3).toFixed(1));
};

export const keyToTest = (tests, key) => (tests || []).find((test) => test?.key === key);

export const testId = (test) => test?._id || test?.id;

// ── Test keys ───────────────────────────────────────────────────────────────
/** The test ID is free text (mirrors EPM_TEST_KEY_MAX_LENGTH on the server). */
export const TEST_KEY_MAX_LENGTH = 40;
export const DEFAULT_KEY_PREFIX = 'WEB';
const KEY_START = 1001;

/** Mirrors the server: one after the highest numbered key with the same prefix. */
export const nextTestKey = (tests, prefix = DEFAULT_KEY_PREFIX) => {
    const numbers = (tests || [])
        .map((test) => String(test?.key || ''))
        .filter((key) => key.startsWith(`${prefix}-`))
        .map((key) => Number(key.slice(prefix.length + 1)))
        .filter(Number.isFinite);
    const next = numbers.length ? Math.max(...numbers) + 1 : KEY_START;
    return `${prefix}-${String(next).padStart(4, '0')}`;
};

// ── Dates — seeded schedules are day offsets from today ────────────────────
export const dayFromToday = (days) => dayjs().startOf('day').add(days, 'day');
export const fmtDay = (days, format = 'D MMM') => dayFromToday(days).format(format);

/** Day X of Y for a live test, from its schedule offsets. */
export const liveProgress = (test) => {
    const start = test?.schedule?.date_time;
    const end = test?.schedule?.end_date_time;
    if (!start || !end || !dayjs(start).isValid() || !dayjs(end).isValid()) return null;
    const total = dayjs(end).startOf('day').diff(dayjs(start).startOf('day'), 'day');
    if (total <= 0) return null;
    const day = Math.min(total, Math.max(1, dayjs().startOf('day').diff(dayjs(start).startOf('day'), 'day') + 1));
    return { day, total };
};

/**
 * The one signal a board card shows for its column (storyboard 10):
 * ICE in Prioritised, duration in Planning, progress in Live, verdict once concluded.
 * Returns { text, tone } where tone is 'warn' for anything that needs someone.
 */
export const cardSignal = (test) => {
    const structure = structureOf(test);
    const { flag, plan, schedule, result } = structure;
    const warn = (text) => ({ text, tone: 'warn' });
    switch (test?.status) {
        case 'IDEAS':
            return flag ? warn(flag) : null;
        case 'PRIORITISED': {
            const ice = iceScore(test);
            if (ice === null) return null;
            return { text: `ICE ${ice.toFixed(1)}${plan?.duration_days > 28 ? ' · slow' : ''}` };
        }
        case 'PLANNING':
            return plan?.duration_days ? { text: `${plan.duration_days} days` } : null;
        case 'BUILD_QA':
            if (flag) return warn(flag);
            return structure.build_note ? { text: structure.build_note } : null;
        case 'SCHEDULED':
            if (flag) return warn(flag);
            return schedule ? { text: `starts ${fmtDay(schedule.start_day)}` } : null;
        case 'LIVE': {
            if (flag === 'guardrail') return warn('guardrail ⚠');
            if (schedule?.note) return { text: schedule.note };
            const progress = liveProgress(test);
            return progress ? { text: `day ${progress.day} / ${progress.total}` } : null;
        }
        case 'ANALYSING':
            if (flag) return warn(flag);
            return structure.analysing_note ? { text: structure.analysing_note } : null;
        case 'CONCLUDED':
            return result ? { text: [result.verdict, result.uplift].filter(Boolean).join(' ') } : null;
        default:
            return null;
    }
};

/**
 * Compares two location maps (locA and locB) against active conflict-enabled fields.
 * Returns an array of matching field objects: { key, label, value, role }.
 */
export const findLocationFieldMatches = (locA = {}, locB = {}, activeFields = []) => {
    if (!activeFields || !activeFields.length) return [];
    const matches = [];

    activeFields.forEach((field) => {
        const valA = locA[field.key];
        const valB = locB[field.key];

        if (
            valA != null &&
            valB != null &&
            String(valA).trim() !== '' &&
            String(valB).trim() !== '' &&
            String(valA).trim().toLowerCase() === String(valB).trim().toLowerCase()
        ) {
            matches.push({
                key: field.key,
                label: field.label || field.key,
                value: String(valA).trim(),
                role: field.conflict_role,
            });
        }
    });

    return matches;
};

